import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { apiFetch } from '../api/client';
import { CardPermissions } from './CardPermissions';
import { DirectBookingPanel } from './DirectBookingPanel';
import { ASSISTANT_NAME } from '../brand.js';

const endpoint = '/v1/onboarding/go-live';
const buttonStyle = { minHeight: '44px', padding: '0.7rem 1rem', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'white', color: 'var(--text-primary)', font: 'inherit', cursor: 'pointer' };
const cardMessages = {
  needs_consent: 'Approve secure card storage below, then reload booking status.',
  read_only: 'This Square account is restricted. Contact support before enabling secure card storage.',
  unavailable: 'We couldn’t verify secure card storage. Reload booking status to try again; contact support if this continues.',
};
const changeMessages = {
  cutoff_missing_or_invalid: 'Square has not supplied a valid cancellation/rescheduling cutoff. Confirm the cutoff in Square Appointments, then reload this status.',
  guest_cancellation_disabled: 'Square does not currently allow clients to cancel appointments online. Review your Square Appointments settings, then reload this status.',
  policy_unrecognized: `${ASSISTANT_NAME} cannot verify this Square cancellation policy. Contact support before enabling self-service changes.`,
  unavailable: 'Square’s cancellation and rescheduling policy could not be checked. Reload this status to try again.',
};

function checkedChangeReadiness(data) {
  const policy = data?.guest_change_readiness;
  if (policy?.status === 'ready' && Number.isInteger(policy.cancellation_window_seconds) && policy.cancellation_window_seconds >= 0 && policy.cancellation_window_seconds <= 2147483647) return policy;
  return { status: Object.hasOwn(changeMessages, policy?.status) ? policy.status : 'unavailable' };
}

function cutoffDescription(seconds) {
  if (seconds === 0) return 'before the appointment begins';
  const unit = seconds % 3600 === 0 ? 'hour' : seconds % 60 === 0 ? 'minute' : 'second';
  const amount = seconds / (unit === 'hour' ? 3600 : unit === 'minute' ? 60 : 1);
  return `more than ${amount} ${unit}${amount === 1 ? '' : 's'} before the appointment begins`;
}

function checkedSnapshot(data) {
  if (!data || typeof data.activated !== 'boolean' || typeof data.booking_enabled !== 'boolean' || typeof data.guest_changes_enabled !== 'boolean' || !Array.isArray(data.blockers) || !data.blockers.every(value => typeof value === 'string') || !Array.isArray(data.policy_fields) || !data.policy_fields.every(field => field && typeof field.label === 'string' && typeof field.value === 'string') || (data.policy_digest != null && typeof data.policy_digest !== 'string')) {
    throw new Error('Booking readiness could not be verified. Reload the status before continuing.');
  }
  return data;
}

export function ActivationPanel({ onActivated }) {
  const headingId = useId();
  const [snapshot, setSnapshot] = useState(null);
  const [consent, setConsent] = useState(false);
  const [guestChanges, setGuestChanges] = useState(false);
  const [busy, setBusy] = useState('loading');
  const [error, setError] = useState('');
  const [reloadRequired, setReloadRequired] = useState(false);
  const alive = useRef(false);
  const inFlight = useRef(false);

  useEffect(() => {
    alive.current = true;
    reload();
    return () => { alive.current = false; };
  }, []);

  async function reload() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy('loading');
    setError('');
    setConsent(false);
    setGuestChanges(false);
    try {
      const data = checkedSnapshot(await apiFetch(endpoint));
      if (alive.current) {
        setSnapshot(data); setReloadRequired(false);
        if (snapshot && data.activated && data.booking_enabled) onActivated?.();
      }
    } catch (err) {
      if (alive.current) { setError(err.message || 'Could not load booking readiness.'); setReloadRequired(true); }
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy('');
    }
  }

  const canActivate = !!snapshot && !snapshot.activated && snapshot.blockers.length === 0 && typeof snapshot.policy_digest === 'string' && snapshot.policy_digest.trim().length > 0 && snapshot.policy_fields.length > 0 && consent && !reloadRequired;
  const changeReadiness = !busy && !reloadRequired ? checkedChangeReadiness(snapshot) : { status: 'unavailable' };
  const changesReady = changeReadiness.status === 'ready';

  async function activate(event) {
    event.preventDefault();
    if (inFlight.current || !canActivate) return;
    inFlight.current = true;
    setBusy('saving');
    setError('');
    try {
      await apiFetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable_booking: true, policy_digest: snapshot.policy_digest, allow_guest_changes: guestChanges }),
      });
      // Confirm the resulting state from the server, never from the checkbox
      // or a successful transport response alone. Publishing is a separate action.
      const data = checkedSnapshot(await apiFetch(endpoint));
      if (!alive.current) return;
      setSnapshot(data);
      if (data.activated && data.booking_enabled) onActivated?.();
      setConsent(false);
      setGuestChanges(false);
      if (!data.activated) {
        setReloadRequired(true);
        setError('Activation has not been confirmed. Reload booking status before continuing.');
      }
    } catch (err) {
      if (!alive.current) return;
      setReloadRequired(true);
      setError(err.status === 409
        ? 'Booking readiness or policies changed. Your review is still shown below. Reload the status and review it again before enabling bookings.'
        : `${err.message || 'Activation could not be confirmed.'} Reload booking status before trying again.`);
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy('');
    }
  }

  return <section class="card" aria-labelledby={headingId} style={{ padding: '1.25rem', lineHeight: 1.6 }}>
    <h2 id={headingId} style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 0.5rem' }}>Enable salon bookings</h2>
    {busy === 'loading' && <p role="status">Checking booking readiness…</p>}
    {snapshot?.activated && snapshot.booking_enabled && <div role="status">
      <p>Bookings are enabled. Use the booking site editor below to publish your salon’s page and share its link.</p>
      <p>{snapshot.guest_changes_enabled
        ? changesReady
          ? `Verified guests can request cancellation or rescheduling ${cutoffDescription(changeReadiness.cancellation_window_seconds)}. ${ASSISTANT_NAME} checks Square’s current policy for each request. The salon handles any fees and refunds.`
          : `Self-service cancellation and rescheduling are unavailable until Square’s change policy can be verified. ${changeMessages[changeReadiness.status]} Clients should contact the salon for changes. The salon handles any fees and refunds.`
        : 'Guest cancellation and rescheduling are not enabled. Clients should contact the salon for changes.'}</p>
    </div>}
    {snapshot?.activated && !snapshot.booking_enabled && <p role="status">{snapshot.card_permissions_required ? `${cardMessages[snapshot.card_permission_status] || cardMessages.unavailable} Your settings and billing controls remain available.` : snapshot.card_policy_review_required ? `Review your booking policies with ${ASSISTANT_NAME} before accepting new appointments. Use Review card requirement or Talk to ${ASSISTANT_NAME} in Your salon. Your settings and billing controls remain available.` : 'Booking access is restricted. Contact support to review the restriction. Your settings and billing controls remain available.'}</p>}
    {snapshot?.card_permissions_required && <CardPermissions />}
    {snapshot && !snapshot.activated && <form onSubmit={activate}>
      <p>Review the policies {ASSISTANT_NAME} has learned before allowing clients to book with your salon.</p>
      {snapshot.blockers.length > 0 && <div role="status">
        <p style={{ fontWeight: 600 }}>Before you can enable bookings:</p>
        <ul style={{ paddingLeft: '1.25rem' }}>{snapshot.blockers.map((blocker, index) => <li key={index}>{blocker}</li>)}</ul>
      </div>}
      {snapshot.policy_fields.length > 0 ? <dl style={{ margin: '1rem 0', padding: '1rem', background: 'var(--cream)', borderRadius: '10px' }}>
        {snapshot.policy_fields.map((field, index) => <div key={index} style={{ marginBottom: index === snapshot.policy_fields.length - 1 ? 0 : '0.875rem' }}>
          <dt style={{ fontWeight: 600 }}>{field.label}</dt>
          <dd style={{ margin: '0.125rem 0 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{field.value}</dd>
        </div>)}
      </dl> : <p>There are no complete policies available to review yet.</p>}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={consent} disabled={!!busy || reloadRequired} onChange={event => setConsent(event.currentTarget.checked)} style={{ marginTop: '0.35rem', minWidth: '18px', minHeight: '18px' }} />
        <span>I have reviewed these policies and approve them. Enable client bookings for my salon.</span>
      </label>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.75rem 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={guestChanges} disabled={!!busy || reloadRequired || !changesReady} onChange={event => setGuestChanges(event.currentTarget.checked)} style={{ marginTop: '0.35rem', minWidth: '18px', minHeight: '18px' }} />
        <span>Also let verified guests cancel or reschedule before the salon’s cutoff. The salon still handles cancellation fees and refunds.</span>
      </label>
      <p>{changesReady
        ? `Square currently allows changes ${cutoffDescription(changeReadiness.cancellation_window_seconds)}. Each request is checked again.`
        : `${changeMessages[changeReadiness.status]} You can still enable new bookings; clients will need to contact the salon for changes.`}</p>
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Enabling bookings does not publish your booking page. Publish it separately in the editor below when you are ready.</p>
      <p>If chat is unavailable, clients can book directly through your Square booking page. Square handles its payment requirements and confirmation.</p>
      <button type="submit" disabled={!!busy || !canActivate} style={{ ...buttonStyle, background: 'var(--sage)', color: 'white', marginTop: '0.5rem' }}>{busy === 'saving' ? 'Enabling bookings…' : 'Enable bookings'}</button>
    </form>}
    {error && <p role="alert" style={{ color: 'var(--coral)' }}>{error}</p>}
    <button type="button" onClick={reload} disabled={!!busy} style={{ ...buttonStyle, marginTop: '0.75rem' }}>Reload booking status</button>
    {snapshot?.activated && <DirectBookingPanel />}
  </section>;
}
